import React, { useState, useEffect, useCallback } from 'react';
import AdminShell from '../components/layout/AdminShell';
import { PageHeader, CollapsibleSection } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import api from '../services/api';
import { toast } from 'sonner';
import { Settings2 } from 'lucide-react';

export default function AccountCodeSetupPage() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/api/system/account-code-segments'); setSegments(res.data); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, field, val) =>
    setSegments(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(segments.map(s =>
        api.put(`/api/system/account-code-segments/${s.segment_number}`, s)
      ));
      toast.success('Account code segments saved — labels will update across the system immediately.');
    } catch (err) { if (err?.response?.status >= 500) toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <AdminShell>
      <PageHeader
        title="Account Code Setup"
        description="Configure the 7 account code segment labels and validation rules. These labels appear throughout the system."
        helpGuide="account-codes"
        actions={
          <Button onClick={saveAll} disabled={saving || loading} data-testid="ac-setup-save">
            {saving ? 'Saving...' : 'Save All Segments'}
          </Button>
        }
      />

      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
        <Settings2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <strong>Admin Note:</strong> These 7 segments are stored internally as <em>Account Code 1–7</em>.
          The labels you set here (e.g. "GL", "Cost Centre", "Restaurant") are displayed on all user-facing
          screens, reports, and user default code fields. Toggle inactive to hide a segment from the system.
        </div>
      </div>

      <CollapsibleSection title="Account Code Segments (1 – 7)">
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            {segments.map((seg, idx) => (
              <div key={seg.segment_number} className="grid grid-cols-12 gap-4 items-center py-4 border-b last:border-0">
                <div className="col-span-1 flex items-center justify-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    seg.active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                  }`}>{seg.segment_number}</div>
                </div>
                <div className="col-span-3 space-y-1">
                  <Label htmlFor={`label-${idx}`}>Display Label</Label>
                  <Input
                    id={`label-${idx}`}
                    value={seg.label}
                    onChange={e => update(idx, 'label', e.target.value)}
                    placeholder={`Account Code ${seg.segment_number}`}
                    disabled={!seg.active}
                    data-testid={`seg-label-${seg.segment_number}`}
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label htmlFor={`hint-${idx}`}>Placeholder / Hint</Label>
                  <Input
                    id={`hint-${idx}`}
                    value={seg.placeholder || ''}
                    onChange={e => update(idx, 'placeholder', e.target.value)}
                    placeholder="e.g. Enter GL code"
                    disabled={!seg.active}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor={`val-${idx}`}>Validation Pattern</Label>
                  <Input
                    id={`val-${idx}`}
                    value={seg.validation_pattern || ''}
                    onChange={e => update(idx, 'validation_pattern', e.target.value)}
                    placeholder="Regex (optional)"
                    disabled={!seg.active}
                  />
                </div>
                <div className="col-span-2 flex items-end gap-4 pb-0.5">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`req-${idx}`}
                      checked={seg.required}
                      onCheckedChange={v => update(idx, 'required', v)}
                      disabled={!seg.active}
                    />
                    <Label htmlFor={`req-${idx}`} className="text-xs">Required</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`act-${idx}`}
                      checked={seg.active}
                      onCheckedChange={v => update(idx, 'active', v)}
                      data-testid={`seg-active-${seg.segment_number}`}
                    />
                    <Label htmlFor={`act-${idx}`} className="text-xs">Active</Label>
                  </div>
                </div>
                <div className="col-span-1 text-center">
                  <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                    ac_{seg.segment_number}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>
    </AdminShell>
  );
}
